import { CircleHelp, Send, XCircle } from "lucide-react";
import { useState } from "react";
import { cn } from "../../lib/utils";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Textarea,
  Tooltip,
} from "../ui";

type CancelVoteReason = "server_issue" | "game_bug" | "connection_issue" | "unfair_start" | "other";

type CancelVoteButtonProps = {
  totalPlayers: number;
  threshold: number;
  canInitiate: boolean;
  disabledMessage: string | null;
  votesRemaining: number;
  isPending: boolean;
  onStartVote: (input: { reason: CancelVoteReason; reasonOtherText?: string }) => Promise<void>;
};

const reasonOptions: Array<{
  value: CancelVoteReason;
  label: string;
  description: string;
}> = [
  {
    value: "server_issue",
    label: "Server issue",
    description: "Match server down, unstable, or unreachable.",
  },
  {
    value: "game_bug",
    label: "Game-breaking bug",
    description: "A severe bug makes the match unplayable.",
  },
  {
    value: "connection_issue",
    label: "Mass disconnect",
    description: "Multiple players cannot stay connected.",
  },
  {
    value: "unfair_start",
    label: "Unfair start",
    description: "Start conditions were clearly invalid.",
  },
  {
    value: "other",
    label: "Other",
    description: "Provide extra context for moderation and teammates.",
  },
];

export function CancelVoteButton({
  totalPlayers,
  threshold,
  canInitiate,
  disabledMessage,
  votesRemaining,
  isPending,
  onStartVote,
}: CancelVoteButtonProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [reason, setReason] = useState<CancelVoteReason>("server_issue");
  const [reasonOtherText, setReasonOtherText] = useState("");

  const requiredYesVotes = Math.ceil(totalPlayers * threshold);
  const requiresOtherText = reason === "other";
  const trimmedOtherText = reasonOtherText.trim();
  const canSubmit = !isPending && (!requiresOtherText || trimmedOtherText.length > 0);

  const startVote = async () => {
    if (!canSubmit) {
      return;
    }

    await onStartVote({
      reason,
      reasonOtherText: requiresOtherText ? trimmedOtherText : undefined,
    });
    setReason("server_issue");
    setReasonOtherText("");
    setIsDialogOpen(false);
  };

  const button = (
    <button
      type="button"
      onClick={() => setIsDialogOpen(true)}
      disabled={!canInitiate || isPending}
      className={cn(
        "rounded-xl border px-4 py-3 flex items-center gap-2.5 text-left transition-colors",
        !canInitiate || isPending
          ? "border-surface-700/60 bg-surface-800/30 text-surface-500 cursor-not-allowed"
          : "border-surface-700/40 bg-surface-800/40 hover:bg-surface-700/40",
      )}
    >
      <XCircle
        className={cn(
          "h-4 w-4 shrink-0",
          !canInitiate || isPending ? "text-surface-600" : "text-surface-400",
        )}
      />
      <div className="min-w-0">
        <p
          className={cn(
            "text-xs font-display font-bold uppercase tracking-wider",
            !canInitiate || isPending ? "text-surface-500" : "text-surface-300",
          )}
        >
          Vote to Cancel
        </p>
        <p className="text-xs text-surface-500 mt-0.5">
          Need {requiredYesVotes} yes votes • {votesRemaining} remaining
        </p>
      </div>
    </button>
  );

  return (
    <>
      {disabledMessage ? (
        <Tooltip content={disabledMessage} side="top">
          {button}
        </Tooltip>
      ) : (
        button
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader subtitle="If enough players agree, this match will be voided with no rating changes.">
            Start Cancel Vote
          </DialogHeader>

          <DialogBody className="space-y-4">
            <div className="rounded-lg border border-surface-700/50 bg-surface-800/40 p-3 text-sm text-surface-300">
              <div className="flex items-start gap-2">
                <CircleHelp className="h-4 w-4 mt-0.5 text-brand-400 shrink-0" />
                <p>
                  Threshold: <span className="font-semibold text-white">{requiredYesVotes}</span> of{" "}
                  {totalPlayers} players ({Math.round(threshold * 100)}%).
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-surface-400">
                Select reason
              </p>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {reasonOptions.map((option) => {
                  const selected = option.value === reason;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setReason(option.value)}
                      className={cn(
                        "rounded-md border px-3 py-2 text-left transition-colors",
                        selected
                          ? "border-brand-500/60 bg-brand-500/10"
                          : "border-surface-700 bg-surface-800/40 hover:border-surface-500",
                      )}
                    >
                      <p
                        className={cn(
                          "text-sm font-semibold",
                          selected ? "text-brand-300" : "text-white",
                        )}
                      >
                        {option.label}
                      </p>
                      <p className="text-xs text-surface-400 mt-1">{option.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {requiresOtherText && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-surface-400">
                  Additional details
                </p>
                <Textarea
                  rows={3}
                  value={reasonOtherText}
                  onChange={(event) => setReasonOtherText(event.target.value)}
                  placeholder="Describe why this match should be cancelled..."
                  resize="none"
                />
                <p className="text-xs text-surface-500">{trimmedOtherText.length}/500 characters</p>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setIsDialogOpen(false)}
              disabled={isPending}
              className="min-w-24"
            >
              Keep Playing
            </Button>
            <Button
              onClick={() => void startVote()}
              disabled={!canSubmit}
              className="min-w-32"
              variant="danger"
            >
              <Send className="h-4 w-4 mr-1" />
              {isPending ? "Starting..." : "Start Vote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
